import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { sanitizeHtmlForPrint } from './sanitizeHtml';

// Browsers cap canvas height around 16,384 px. Stay safely under that so
// large receipts (e.g. batch orders with hundreds of cards) don't silently
// produce blank pages when rasterized as a single canvas.
const MAX_CANVAS_HEIGHT_PX = 14000;
const RENDER_SCALE = 2;
const CONTENT_WIDTH_PX = 800;

const waitForImages = (root) => {
    const imgs = Array.from(root.querySelectorAll('img'));
    return Promise.all(
        imgs.map((img) => {
            if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
            return new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
            });
        })
    );
};

const renderSingleCanvasPdf = async (wrappedHtml, name) => {
    const element = document.createElement('div');
    element.innerHTML = wrappedHtml;
    await html2pdf()
        .set({
            filename: `${name}.pdf`,
            html2canvas: { scale: RENDER_SCALE },
            jsPDF: { orientation: 'portrait', unit: 'pt', format: 'a4', compressPDF: true },
        })
        .from(element)
        .save();
};

// Walk the DOM under `root` and return the bottom Y (relative to root) of
// every element. These are the candidate page-break positions; choosing
// breaks from this set ensures we never split a leaf element across pages.
const collectBreakCandidates = (root) => {
    const rootTop = root.getBoundingClientRect().top;
    const candidates = new Set([0]);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    while (walker.nextNode()) {
        const rect = walker.currentNode.getBoundingClientRect();
        const bottom = Math.round(rect.bottom - rootTop);
        if (bottom > 0) candidates.add(bottom);
    }
    return [...candidates].sort((a, b) => a - b);
};

// Pack content into pages: each break is the largest candidate <= last+max.
const computePageBreaks = (candidates, totalHeight, maxPageHeight) => {
    const breaks = [0];
    while (breaks[breaks.length - 1] < totalHeight) {
        const last = breaks[breaks.length - 1];
        const target = last + maxPageHeight;
        if (target >= totalHeight) {
            breaks.push(totalHeight);
            break;
        }
        let next = null;
        for (let i = candidates.length - 1; i >= 0; i--) {
            if (candidates[i] <= target && candidates[i] > last) {
                next = candidates[i];
                break;
            }
        }
        // No element boundary fits — fall back to a hard pixel break so we
        // don't loop forever on a single oversized element.
        breaks.push(next ?? target);
    }
    return breaks;
};

const renderPaginatedPdf = async (inner, name) => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const pageWidthPt = pdf.internal.pageSize.getWidth();
    const pageHeightPt = pdf.internal.pageSize.getHeight();
    const pageContentHeightPx = Math.floor(CONTENT_WIDTH_PX * (pageHeightPt / pageWidthPt));

    inner.style.width = `${CONTENT_WIDTH_PX}px`;

    // Stage inner once on-screen to measure break candidates.
    const measureClip = document.createElement('div');
    measureClip.style.cssText =
        'position:fixed;top:0;left:0;z-index:-1;pointer-events:none;' +
        `width:${CONTENT_WIDTH_PX}px;height:auto;overflow:visible;background:#fff;`;
    inner.style.marginTop = '0';
    measureClip.appendChild(inner);
    document.body.appendChild(measureClip);
    const totalHeightPx = inner.scrollHeight;
    const candidates = collectBreakCandidates(inner);
    const breaks = computePageBreaks(candidates, totalHeightPx, pageContentHeightPx);
    document.body.removeChild(measureClip);

    // Group consecutive page breaks into chunks that fit in one canvas.
    // Each chunk runs html2canvas once (which creates an internal iframe —
    // the expensive part), then we slice the resulting canvas locally into
    // individual PDF pages. For a 450-card receipt this drops the iframe
    // count from ~45 to ~8.
    const maxChunkHeightPx = Math.floor(MAX_CANVAS_HEIGHT_PX / RENDER_SCALE);
    const chunks = [];
    let chunkStart = 0;
    for (let i = 1; i < breaks.length; i++) {
        if (breaks[i] - breaks[chunkStart] > maxChunkHeightPx && i - 1 > chunkStart) {
            chunks.push({ startIdx: chunkStart, endIdx: i - 1 });
            chunkStart = i - 1;
        }
    }
    chunks.push({ startIdx: chunkStart, endIdx: breaks.length - 1 });

    // The clip must be on-screen for html2canvas to render it (its default
    // rendering window starts at the viewport, so off-screen elements come
    // out blank). z-index: -1 keeps it visually hidden behind page content.
    let pageIdx = 0;
    for (const chunk of chunks) {
        const sliceStart = breaks[chunk.startIdx];
        const sliceHeight = breaks[chunk.endIdx] - sliceStart;

        const clip = document.createElement('div');
        clip.style.cssText =
            'position:fixed;top:0;left:0;z-index:-1;pointer-events:none;' +
            `width:${CONTENT_WIDTH_PX}px;height:${sliceHeight}px;` +
            'overflow:hidden;background:#fff;';
        inner.style.marginTop = `${-sliceStart}px`;
        clip.appendChild(inner); // re-parents inner into the current clip
        document.body.appendChild(clip);

        // eslint-disable-next-line no-await-in-loop
        const chunkCanvas = await html2canvas(clip, {
            scale: RENDER_SCALE,
            backgroundColor: '#fff',
            useCORS: true,
            logging: false,
        });

        document.body.removeChild(clip);

        // Slice this chunk's canvas into individual PDF pages.
        for (let i = chunk.startIdx; i < chunk.endIdx; i++) {
            const pageStartPx = breaks[i] - sliceStart;
            const pageHeightPx = breaks[i + 1] - breaks[i];
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = chunkCanvas.width;
            pageCanvas.height = pageHeightPx * RENDER_SCALE;
            const ctx = pageCanvas.getContext('2d');
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            ctx.drawImage(
                chunkCanvas,
                0, pageStartPx * RENDER_SCALE,
                chunkCanvas.width, pageCanvas.height,
                0, 0,
                chunkCanvas.width, pageCanvas.height,
            );

            if (pageIdx > 0) pdf.addPage();
            // Scale the slice to fit page width; height is proportional and
            // may be less than the full page (white space at bottom is OK).
            const imgHeightPt = pageHeightPx * (pageWidthPt / CONTENT_WIDTH_PX);
            pdf.addImage(
                pageCanvas.toDataURL('image/jpeg', 0.92),
                'JPEG',
                0, 0,
                pageWidthPt, imgHeightPt,
            );
            pageCanvas.width = 0;
            pageCanvas.height = 0;
            pageIdx++;
        }

        chunkCanvas.width = 0;
        chunkCanvas.height = 0;
        // Yield to the event loop so the UI stays responsive between chunks.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 0));
    }

    pdf.save(`${name}.pdf`);
};

const downloadAsPdf = async (sanitizedHtml, name) => {
    // Receipt templates declare `body { padding: 20px; ... }` in their
    // <style> block. When injected into the host document via innerHTML
    // those rules apply globally and shift the host page's <body>. Scope
    // them to a wrapper class so they only affect the PDF render tree.
    const scopedHtml = sanitizedHtml.replace(/\bbody\s*\{/g, '.pdf-receipt-host{');
    const wrappedHtml = `<div class="pdf-receipt-host" style="line-height: 1.2;">${scopedHtml}</div>`;

    // Render off-screen to measure total height and wait for images (e.g. QR code).
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;left:-99999px;top:0;width:${CONTENT_WIDTH_PX}px;`;
    probe.innerHTML = wrappedHtml;
    document.body.appendChild(probe);

    try {
        await waitForImages(probe);
        const inner = probe.firstElementChild;
        const totalHeightPx = inner.scrollHeight;

        if (totalHeightPx * RENDER_SCALE <= MAX_CANVAS_HEIGHT_PX) {
            document.body.removeChild(probe);
            await renderSingleCanvasPdf(wrappedHtml, name);
            return;
        }

        // Detach from probe; renderPaginatedPdf re-parents it into per-page clips.
        probe.removeChild(inner);
        document.body.removeChild(probe);
        await renderPaginatedPdf(inner, name);
    } catch (err) {
        if (probe.parentNode) probe.parentNode.removeChild(probe);
        throw err;
    }
};

export const Print = async (html, name = 'Print', height = 600, width = 800, download = false) => {
    const sanitizedHtml = sanitizeHtmlForPrint(html);
    if (download) {
        await downloadAsPdf(sanitizedHtml, name);
    } else {
        const printWindow = window.open('', '', `height=${height},width=${width}`);
        printWindow.document.open();
        printWindow.document.write(sanitizedHtml);
        setTimeout(function (){
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
        }, 1000);
    }
};
